import React from "react";
import {
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  FormControl,
  FormLabel,
  Select,
  Option,
  Button,
  Typography,
} from "@mui/joy";
import { useAssignments } from "../../../contexts/controllers/useAssignments";

interface AssignmentExportModalProps {
  open: boolean;
  onClose: () => void;
}

const AssignmentExportModal: React.FC<AssignmentExportModalProps> = ({ open, onClose }) => {
  const [selectedYear, setSelectedYear] = React.useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = React.useState<number>(new Date().getMonth() + 1);
  const [isExporting, setIsExporting] = React.useState(false);

  const { exportAssignmentsByMonth } = useAssignments();

  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const endYear = Math.max(currentYear, startYear); // At least 2025
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  // Months with display labels
  const months = [
    { value: 1, label: "Janvier" },
    { value: 2, label: "Février" },
    { value: 3, label: "Mars" },
    { value: 4, label: "Avril" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Juin" },
    { value: 7, label: "Juillet" },
    { value: 8, label: "Août" },
    { value: 9, label: "Septembre" },
    { value: 10, label: "Octobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Décembre" },
  ];

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const csvBlob = await exportAssignmentsByMonth(selectedYear, selectedMonth);

      if (csvBlob) {
        // Create a download link
        const url = URL.createObjectURL(csvBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `assignments_${selectedYear}_${selectedMonth.toString().padStart(2, "0")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        onClose();
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog variant="outlined" role="alertdialog">
        <ModalClose disabled={isExporting} />
        <Typography level="h2">Exporter les prestations</Typography>
        <Typography level="body-md">
          Sélectionnez le mois et l'année pour exporter les prestations en format CSV.
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel>Année</FormLabel>
            <Select
              value={selectedYear}
              onChange={(_, value) => setSelectedYear(value as number)}
              disabled={isExporting}
            >
              {years.map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Mois</FormLabel>
            <Select
              value={selectedMonth}
              onChange={(_, value) => setSelectedMonth(value as number)}
              disabled={isExporting}
            >
              {months.map((month) => (
                <Option key={month.value} value={month.value}>
                  {month.label}
                </Option>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={handleClose} disabled={isExporting}>
              Annuler
            </Button>
            <Button onClick={handleExport} loading={isExporting} disabled={isExporting}>
              Télécharger CSV
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default AssignmentExportModal;
